import React from "react";

export type CardAccent = "none" | "blue" | "amber" | "green" | "red";

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Colored hover border + glow. */
  accent?: CardAccent;
  /** Enables lift + glow on hover (for clickable cards). */
  interactive?: boolean;
  /** Removes default padding (e.g. cards that contain a full-bleed image). */
  flush?: boolean;
  children?: React.ReactNode;
}

const accentHover: Record<CardAccent, string> = {
  none: "hover:border-gray-600",
  blue: "hover:border-blue-500/70 hover:shadow-glow",
  amber: "hover:border-accent-500/70 hover:shadow-glow-accent",
  green: "hover:border-emerald-500/70",
  red: "hover:border-red-500/70",
};

/**
 * Surface panel primitive. Replaces the repeated
 * `bg-gray-800 rounded-xl border border-gray-700 …` markup across the app.
 */
const Card: React.FC<CardProps> = ({
  accent = "none",
  interactive = false,
  flush = false,
  className = "",
  children,
  ...rest
}) => {
  const interactiveCls = interactive
    ? `transition-all duration-300 ease-out hover:-translate-y-1 ${accentHover[accent]} cursor-pointer`
    : accent !== "none"
    ? accentHover[accent]
    : "";

  return (
    <div
      className={[
        "relative rounded-2xl border border-gray-700/80 bg-gray-800/90",
        "shadow-lg shadow-black/20",
        flush ? "overflow-hidden" : "p-6",
        interactiveCls,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      {...rest}
    >
      {children}
    </div>
  );
};

export default Card;
